import React, { PropsWithChildren } from 'react';

import { css } from '@emotion/core';
import { css as eCss } from '@emotion/css';
import styled, { StyledComponent } from '@emotion/styled';
import { Modal as AModal, ModalFuncProps, ModalProps } from 'antd';
import { tuple } from 'antd/lib/_util/type';
import { ModalFunc } from 'antd/lib/modal/confirm';

export const tYzTypeModal = tuple('confirm', 'searchModal', 'hpModal');
export const tStyledModal = tuple('info', 'confirm', 'warn', 'warning', 'error', 'success', 'deleted');

type TYzTypeModal = typeof tYzTypeModal[number] | undefined;

export type TStyledModalProps = ModalProps & {
  yzTypeModal?: TYzTypeModal;
  children?: React.ReactNode;
};

type TStyledModal = StyledComponent<PropsWithChildren<TStyledModalProps>> &
  Record<typeof tStyledModal[number], ModalFunc>;

const handleModalWithType = ({ yzTypeModal }: TStyledModalProps) => {
  switch (yzTypeModal) {
    /**
     * stylelint(no-empty-source) 빈칸으로 css를 사용할 수 없음
     * @see https://stylelint.io/user-guide/rules/no-empty-source
     */
    case 'searchModal':
      return css`
        .ant-modal-header {
          border-bottom: 0;
          padding-bottom: 0;
        }
      `;
    case 'hpModal':
      return css`
        .ant-modal-body {
          padding: 0;
        }

        .ant-modal-close-x {
          width: 70px;
          height: 70px;
          line-height: 70px;
        }
      `;
    case 'confirm':
      return css`
        .ant-modal-body {
          padding: 40px;
        }
        .ant-modal-close-x {
          display: none;
        }
        .ant-modal-footer {
          text-align: center;
          height: 69px;
          padding: 0;
          border-top: 0;
        }
        .ant-btn {
          display: inline-block;
          margin: 0;
          width: 50%;
          height: 100%;
        }
        .ant-btn.ant-btn-primary {
          margin: 0 !important;
        }
      `;
    default:
      return ``;
  }
};

const CModal = styled(AModal)<TStyledModalProps>`
  ${handleModalWithType}
` as TStyledModal;

export const Modal = ({ ...props }: TStyledModalProps) => {
  switch (props.yzTypeModal) {
    case 'confirm':
      return <CModal {...props} width={props.width || '420px'} />;
    default:
      return <CModal {...props} />;
  }
};

/**
 * 해상도 무관 스타일
 */
const commonStyle = eCss`
& > .ant-modal-content > .ant-modal-body {
  padding: 0;

  .ant-modal-confirm-content {
    margin-top: 0;
    font-size: 17px;
  }

  .ant-modal-confirm-btns {
    margin: 0;
    width: 100%;
  }
}
`;

const oneButtonStyle = eCss`
& > .ant-modal-content > .ant-modal-body .ant-modal-confirm-btns > button {
  width: 100%;
  height: 69px;
}
`;

const twoButtonStyle = eCss`
& > .ant-modal-content > .ant-modal-body .ant-modal-confirm-btns > button {
  width: 50%;
  height: 69px;
  margin-left: 0;
}
`;

/**
 * 1080p 이하 해상도의 작은 사이즈 모달
 */
const fullHD = eCss`
  @media (max-width: 1920px) {
    width: 420px !important;

    & > .ant-modal-content > .ant-modal-body .ant-modal-confirm-content {
      padding: 40px;
    }
  }
`;

/**
 * 1080p 초과 해상도의 작은 사이즈 모달
 */
const ultraHD = eCss`
@media (min-width: 1921px) {
  width: 480px !important;

  & > .ant-modal-content > .ant-modal-body .ant-modal-confirm-content {
    padding: 50px;
  }
}
`;

const buttonOption = {
  icon: null,
  maskClosable: true,
};

const oneButtonOption = {
  className: [commonStyle, oneButtonStyle, fullHD, ultraHD].join(' '),
};

const twoButtonOption = {
  className: [commonStyle, twoButtonStyle, fullHD, ultraHD].join(' '),
};

const info = (props: ModalFuncProps) =>
  AModal.info({
    ...buttonOption,
    ...oneButtonOption,
    ...props,
  });

const confirm = (props: ModalFuncProps) =>
  AModal.confirm({
    ...buttonOption,
    ...twoButtonOption,
    ...props,
  });

const warning = (props: ModalFuncProps) =>
  AModal.warning({
    ...buttonOption,
    ...oneButtonOption,
    okButtonProps: {
      danger: true,
    },
    ...props,
  });

const error = (props: ModalFuncProps) =>
  AModal.error({
    ...buttonOption,
    ...oneButtonOption,
    okButtonProps: {
      danger: true,
    },
    ...props,
  });

const success = (props: ModalFuncProps) =>
  AModal.success({
    ...buttonOption,
    ...oneButtonOption,
    ...props,
  });
const deleted = (props: ModalFuncProps) =>
  AModal.success({
    ...buttonOption,
    ...oneButtonOption,
    ...props,
  });

Modal.info = info;
Modal.confirm = confirm;
Modal.warn = warning;
Modal.warning = warning;
Modal.error = error;
Modal.success = success;
Modal.deleted = deleted;
